// Lógica pura del heatmap "Ventas en salón por franja horaria" (sin dependencias de
// React/Tremor para que sea testeable desde Node). La participación de cada
// franja se calcula SOBRE EL TOTAL DEL DÍA, nunca sobre el total semanal.

import { PALETTE } from "./palette.ts";

export interface HeatmapCellInput {
  dia_semana: string;
  franja_horaria: string;
  ventas: number | null; // null = sin dato (se muestra "s/d")
}

export interface HeatmapComputed {
  dias: string[]; // orden fijo Lunes..Domingo (solo los presentes en data)
  franjas: string[]; // orden cronológico según inicio de rango
  // matrix[dia][franja] = { pct: number|null, ventas: number|null, sinDato: boolean }
  matrix: Record<string, Record<string, { pct: number | null; ventas: number | null; sinDato: boolean }>>;
  min: number;
  max: number;
  filasOk: boolean; // cada fila suma ≈100%
}

export const DIAS_ORDEN = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

export function computeHeatmap(raw: HeatmapCellInput[]): HeatmapComputed {
  // Días (filas): si las etiquetas son días de semana estándar, respetar el
  // orden cronológico Lunes..Domingo. Si son otra dimensión (ej. meses en el
  // heatmap "Ventas en salón por franja horaria"), usar el orden de aparición.
  const etiquetas = Array.from(new Set(raw.map((r) => r.dia_semana)));
  const tieneDiasSemana = etiquetas.some((e) => DIAS_ORDEN.includes(e));
  const dias = tieneDiasSemana
    ? DIAS_ORDEN.filter((d) => etiquetas.includes(d))
    : etiquetas;

  // Franjas: orden cronológico según la hora de inicio del rango "HH-MM".
  const franjasPresentes = Array.from(new Set(raw.map((r) => r.franja_horaria)));
  const franjaInicio = (f: string) => {
    const m = f.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 999;
  };
  const franjas = franjasPresentes.sort((a, b) => franjaInicio(a) - franjaInicio(b));

  // Totales por día (solo celdas con dato).
  const totalPorDia: Record<string, number> = {};
  for (const r of raw) {
    if (r.ventas == null) continue;
    totalPorDia[r.dia_semana] = (totalPorDia[r.dia_semana] ?? 0) + r.ventas;
  }

  // Matriz de participaciones.
  const matrix: HeatmapComputed["matrix"] = {};
  for (const d of dias) {
    matrix[d] = {};
    const total = totalPorDia[d] ?? 0;
    for (const f of franjas) {
      const cell = raw.find((r) => r.dia_semana === d && r.franja_horaria === f);
      const ventas = cell ? cell.ventas : null;
      const sinDato = ventas == null || total <= 0;
      const pct = sinDato ? null : Math.round((ventas! / total) * 100);
      matrix[d][f] = { pct, ventas, sinDato };
    }
  }

  // Validación silenciosa: cada fila debe sumar ≈100% (tolerancia por redondeo).
  let filasOk = true;
  for (const d of dias) {
    const suma = franjas.reduce((s, f) => s + (matrix[d][f].pct ?? 0), 0);
    if (Math.abs(suma - 100) > 2) {
      filasOk = false;
      // eslint-disable-next-line no-console
      console.warn(`[Heatmap] Fila ${d} suma ${suma}% (esperado ≈100%)`);
    }
  }

  // Min/max para la escala de color (solo celdas con dato).
  const vals: number[] = [];
  for (const d of dias) for (const f of franjas) {
    const p = matrix[d][f].pct;
    if (p != null) vals.push(p);
  }
  const min = vals.length ? Math.min(...vals) : 0;
  const max = vals.length ? Math.max(...vals) : 100;

  return { dias, franjas, matrix, min, max, filasOk };
}

// ── Escala de color: paleta corporativa azul (dinámica) ──
// Usa los 3 primarios de la identidad: claro (primaryLight) -> oscuro (primaryDark).

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

export function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}
export function azulScale(t: number): string {
  // t en [0,1]: 0 = claro (#6F91B3), 1 = oscuro (#15335B)
  const c0 = hexToRgb(PALETTE.primaryLight);
  const c1 = hexToRgb(PALETTE.primaryDark);
  const r = lerp(c0[0], c1[0], t);
  const g = lerp(c0[1], c1[1], t);
  const b = lerp(c0[2], c1[2], t);
  return `rgb(${r}, ${g}, ${b})`;
}
export function colorFor(pct: number | null, min: number, max: number): { bg: string; fg: string } {
  if (pct == null) return { bg: "#f1f5f9", fg: "#94a3b8" }; // sin dato
  const span = max - min || 1;
  const t = (pct - min) / span;
  const bg = azulScale(Math.max(0, Math.min(1, t)));
  // Contraste automático: texto claro si la celda es oscura.
  const fg = t > 0.55 ? "#ffffff" : "#0B2545";
  return { bg, fg };
}
